import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { WatchlistSubNav } from '@/components/watchlist/SubNav';
import { wl } from '@/components/watchlist/theme';

type SourceStatus = 'live' | 'quota' | 'not_wired' | 'phase43';

interface SourceRow {
  name: string;
  access: string;
  capacity: string;
  status: SourceStatus;
  usageKey?: 'tiktok' | 'forums';
}

const SOURCES: SourceRow[] = [
  { name: 'X / Twitter', access: 'RapidAPI · key pool', capacity: 'rotating keys · rate-limited', status: 'live' },
  { name: 'Instagram', access: 'RapidAPI scraper', capacity: 'rotating keys', status: 'live' },
  { name: 'Facebook', access: 'RapidAPI scraper', capacity: 'cursor paging', status: 'live' },
  { name: 'YouTube', access: 'RapidAPI', capacity: 'rotating keys', status: 'live' },
  { name: 'Reddit', access: 'RapidAPI', capacity: 'rotating keys', status: 'live' },
  { name: 'TikTok', access: 'RapidAPI scraper', capacity: '50/month/key', status: 'quota', usageKey: 'tiktok' },
  { name: 'Forums', access: 'RapidAPI', capacity: '50/month/key', status: 'quota', usageKey: 'forums' },
  { name: 'Telegram', access: '—', capacity: '—', status: 'not_wired' },
  { name: 'Bluesky', access: 'Official API', capacity: 'open firehose search', status: 'phase43' },
  { name: 'Mastodon', access: 'Official API', capacity: 'per-instance', status: 'phase43' },
  { name: 'Threads', access: 'Official API', capacity: 'keyword search', status: 'phase43' },
  { name: 'RSS / news', access: 'Feeds', capacity: 'unlimited', status: 'phase43' },
  { name: 'Meta Ad Library', access: 'Public API', capacity: '—', status: 'phase43' },
  { name: 'App stores (AppINT)', access: 'Store search', capacity: '—', status: 'phase43' },
  { name: 'Certificate transparency', access: 'crt.sh scheduled', capacity: '—', status: 'phase43' },
  { name: 'Maigret username sweep', access: 'Local tool', capacity: '—', status: 'phase43' },
];

const STATUS_META: Record<SourceStatus, { label: string; color: string }> = {
  live: { label: 'LIVE', color: wl.green },
  quota: { label: 'LIVE', color: wl.green },
  not_wired: { label: 'NOT WIRED', color: wl.textFaint },
  phase43: { label: 'PHASE 4.3', color: wl.medium },
};

const PAYMENT_PATTERNS = [
  { rail: 'UPI VPA', region: 'India', live: true },
  { rail: 'IBAN', region: 'International', live: true },
  { rail: 'PayPal.me links', region: 'International', live: true },
  { rail: 'Crypto wallets (ETH/TRON/BTC)', region: 'Global', live: true },
  { rail: 'Sort code + account', region: 'UK', live: false },
];

export default function SourcesPage() {
  const usageQuery = useQuery({ queryKey: ['watchlist-source-usage'], queryFn: () => watchlistApi.getSourceUsage() });
  const keywordSetsQuery = useQuery({ queryKey: ['watchlist-keyword-sets'], queryFn: () => watchlistApi.listKeywordSets() });

  const activeLanguages = useMemo(() => {
    const sets = keywordSetsQuery.data?.keyword_sets ?? [];
    const langs = new Map<string, number>();
    sets.forEach((s) => s.variants.forEach((v) => langs.set(v.language_code, (langs.get(v.language_code) ?? 0) + 1)));
    return Array.from(langs.entries()).sort((a, b) => b[1] - a[1]);
  }, [keywordSetsQuery.data]);

  return (
    <div style={{ background: wl.bg, minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WatchlistSubNav />
      <div className="mb-5">
        <h1 style={{ color: wl.text, fontWeight: 800, fontSize: 16 }}>Sources & alerts</h1>
        <p style={{ color: wl.textMuted, fontSize: 12, marginTop: 2 }}>What's actually collecting, what's quota-capped, and where alerts go</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }}>
          <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.07em', fontWeight: 700, marginBottom: 12 }}>COLLECTION SOURCES</p>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontSize: 12, fontFamily: wl.font }}>
              <thead>
                <tr style={{ color: wl.textFaint, fontSize: 10.5 }}>
                  <th className="text-left pb-2 font-semibold">SOURCE</th>
                  <th className="text-left pb-2 font-semibold">ACCESS</th>
                  <th className="text-left pb-2 font-semibold">CAPACITY</th>
                  <th className="text-left pb-2 font-semibold">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {SOURCES.map((s) => {
                  const usage = s.usageKey ? usageQuery.data?.usage[s.usageKey] : undefined;
                  return (
                    <tr key={s.name} style={{ borderTop: `1px solid ${wl.borderSoft}` }}>
                      <td className="py-2.5" style={{ color: wl.text, fontWeight: 600 }}>{s.name}</td>
                      <td className="py-2.5" style={{ color: wl.textMuted }}>{s.access}</td>
                      <td className="py-2.5" style={{ color: wl.textMuted }}>
                        {usage ? (
                          usage.available ? (
                            <div style={{ minWidth: 150 }}>
                              <div style={{ height: 5, background: wl.borderSoft, borderRadius: 3, marginBottom: 3 }}>
                                <div style={{ height: '100%', width: `${Math.min(100, ((usage.used ?? 0) / usage.cap) * 100)}%`, background: (usage.used ?? 0) / usage.cap > 0.8 ? wl.critical : wl.medium, borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 10.5 }}>{usage.used}/{usage.cap} this month · resets in {usage.resets_in_days}d</span>
                            </div>
                          ) : <span style={{ fontSize: 10.5 }}>usage unavailable</span>
                        ) : s.capacity}
                      </td>
                      <td className="py-2.5">
                        <span className="px-2 py-0.5 rounded font-bold" style={{ background: `${STATUS_META[s.status].color}18`, color: STATUS_META[s.status].color, fontSize: 10 }}>
                          {STATUS_META[s.status].label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-3" style={{ background: wl.mediumWash, border: `1px solid ${wl.medium}30`, borderRadius: 8 }}>
            <p style={{ color: wl.medium, fontSize: 11.5, lineHeight: 1.6 }}>
              TikTok and Forums are capped by the paid API plan (50 requests/month/key). More keys don't raise a monthly cap — it's a billing decision, so usage is shown here instead of hidden.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.07em', fontWeight: 700, marginBottom: 10 }}>ALERT CHANNELS</p>
            {[
              { name: 'Email', status: 'LIVE', color: wl.green, note: 'Every keyword/brand/VIP alert, 24h cooldown per subject' },
              { name: 'Telegram', status: 'NEXT', color: wl.info, note: 'Per-tenant chat routing — deferred to a later pass' },
              { name: 'Slack', status: 'PHASE 4.3', color: wl.medium, note: 'Channel webhook' },
              { name: 'Generic webhook', status: 'PHASE 4.3', color: wl.medium, note: 'POST JSON to your SIEM/SOAR' },
            ].map((ch) => (
              <div key={ch.name} className="flex items-start justify-between py-2" style={{ borderTop: `1px solid ${wl.borderSoft}` }}>
                <div>
                  <p style={{ color: wl.text, fontSize: 12.5, fontWeight: 600 }}>{ch.name}</p>
                  <p style={{ color: wl.textFaint, fontSize: 10.5, marginTop: 2 }}>{ch.note}</p>
                </div>
                <span className="px-2 py-0.5 rounded font-bold flex-shrink-0" style={{ background: `${ch.color}18`, color: ch.color, fontSize: 9.5 }}>{ch.status}</span>
              </div>
            ))}
          </div>

          <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.07em', fontWeight: 700, marginBottom: 10 }}>PAYMENT-ID PATTERNS</p>
            {PAYMENT_PATTERNS.map((p) => (
              <div key={p.rail} className="flex items-center justify-between py-1.5">
                <span style={{ color: wl.text, fontSize: 12 }}>{p.rail}</span>
                <span style={{ color: p.live ? wl.green : wl.textFaint, fontSize: 10.5, fontWeight: 700 }}>{p.live ? 'ON' : 'OFF'} · {p.region}</span>
              </div>
            ))}
            <p style={{ color: wl.textFaint, fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>Passive pattern matching only — no live rail validation call.</p>
          </div>

          <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '16px 18px' }}>
            <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.07em', fontWeight: 700, marginBottom: 10 }}>LANGUAGE COVERAGE TODAY</p>
            {keywordSetsQuery.isLoading ? (
              <Loader2 className="animate-spin" size={14} style={{ color: wl.green }} />
            ) : activeLanguages.length === 0 ? (
              <p style={{ color: wl.textFaint, fontSize: 11.5 }}>No keyword variants configured yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {activeLanguages.map(([lang, count]) => (
                  <span key={lang} className="px-2 py-1 rounded" style={{ background: wl.bg, color: wl.text, fontSize: 11, border: `1px solid ${wl.borderSoft}` }}>{lang} · {count}</span>
                ))}
              </div>
            )}
            <p style={{ color: wl.textFaint, fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
              No reusable "regional pack" toggle yet — add a language/script variant to any keyword set to cover it today.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
